import customElement from "@/wc/registry/lib/decorator/customElement/index.js";
import type IFormInputTypeComponent from "@/wc/registry/types/form-input-type";
import { show, hide } from "@/wc/registry/lib/dom/index.js";
import StekzFormInputElement from "@/wc/registry/component/form-input-base/index.js";

// Not using a shadowRoot, to secretly make use of the external css
// utilized by c-form-input. (to avoids fetching the same css bundle
// multiple times per c-form-input-[type])
const componentName = "c-form-input-string";
@customElement(componentName, {
    style: fetch("/wc/registry/component/form-input-string/styles.css").then(
        (res) => res.text()
    ),
    template: fetch(
        "/wc/registry/component/form-input-string/template.html"
    ).then((res) =>
        res.text().then((html) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            return doc.querySelector(`[data-component=${componentName}]`);
        })
    ),
    shadowRoot: false,
})
export default class FormInputString
    extends StekzFormInputElement
    implements IFormInputTypeComponent
{
    usedProps = [
        "value",
        "label",
        "placeholder",
        "required",
        "horizontal",
        "readOnly",
        "helperText",
        "appendValue",
        "prependValue",
        "inline",
    ];

    isInline = false;

    static get observedAttributes() {
        return [
            "data-value",
            "data-read-only",
            "data-append-value",
            "data-prepend-value",
        ];
    }

    // When the value attribute is changed, be sure to update the nested nodes
    // to reflect this change when they exist.
    attributeChangedCallback(
        name: string,
        _oldValue: string,
        newValue: string
    ) {
        if (name === "data-value" && this.nodes.input) {
            this.value = newValue;
        }

        if (name === "data-read-only") {
            this.readOnly = newValue;
        }

        if (name === "data-append-value" && this.nodes.input) {
            this.appendValue = newValue;
        }

        if (name === "data-prepend-value" && this.nodes.input) {
            this.prependValue = newValue;
        }
    }

    handleEvent() {
        // Only listening to the 'change' event at the moment. When this happens,
        // the form input change is 'committed' to the form input.
        this.reset();
        this.value = this.nodes.input.value;
    }

    set name(value: string) {
        this.dataset.name = value;
    }

    set horizontal(value: string) {
        if (value !== undefined) {
            this.nodes["field-wrapper"].classList.add("field-wrapper");
            this.nodes["label-wrapper"].classList.add("label-wrapper");
        }
    }

    set placeholder(value: string) {
        if (this.nodes.input) {
            this.nodes.input.setAttribute("placeholder", value);
        }
    }

    set helperText(value: string) {
        if (this.nodes.input && value) {
            const errorElement = this.nodes["help-text"];

            const pElement = document.createElement("p");
            pElement.classList.add("help", "has-text-grey", "mb-0");
            pElement.innerText = value;

            errorElement.parentNode.insertBefore(pElement, errorElement);
        }
    }

    set inline(value: string) {
        this.isInline = value !== "false" && value !== undefined;
    }

    set readOnly(value: string) {
        if (!this.nodes.input) {
            return;
        }

        if (value === undefined || value === "null") {
            this.nodes.input.removeAttribute("readonly");
        } else {
            this.nodes.input.setAttribute("readonly", "");
        }
    }

    set appendValue(value: string) {
        if (value !== undefined) {
            if (this.querySelector(".append-prepend-wrapper")) {
                const childElement = this.querySelector(".append-element");
                childElement.innerText = value;
            } else {
                const appendWrapper = document.createElement("div");
                appendWrapper.classList.add("append-prepend-wrapper");
                const childElement = document.createElement("div");
                childElement.innerText = value;
                childElement.classList.add("append-element");
                appendWrapper.appendChild(this.nodes.input);
                appendWrapper.appendChild(childElement);
                this.nodes.control.insertAdjacentElement(
                    "afterbegin",
                    appendWrapper
                );
            }
        }
    }
    set prependValue(value: string) {
        if (value !== undefined) {
            if (this.querySelector(".append-prepend-wrapper")) {
                const childElement = this.querySelector(".prepend-element");
                childElement.innerText = value;
            } else {
                const prependWrapper = document.createElement("div");
                prependWrapper.classList.add("append-prepend-wrapper");
                const childElement = document.createElement("div");
                childElement.innerText = value;
                childElement.classList.add("prepend-element");
                prependWrapper.appendChild(childElement);
                prependWrapper.appendChild(this.nodes.input);
                this.nodes.control.insertAdjacentElement(
                    "afterbegin",
                    prependWrapper
                );
            }
        }
    }

    set value(value: string | undefined) {
        if (value === undefined) {
            return;
        }

        if (this.nodes.input && value !== undefined) {
            this.nodes.input.value = value;
        }

        // Let the form know that we've changed our value. It might be that there
        // are other form-inputs depending on the value of this form-input.
        // The form will then inform the right c-form-inputs that they have to
        // re-fetch their values.
        this.dispatchEvent(
            new CustomEvent("c-form-input-string:changed", {
                bubbles: true,
                composed: true,
                detail: {
                    name: this.dataset.name,
                    value: this.value,
                },
            })
        );
    }

    get value(): string {
        let value = this.dataset.value;
        if (this.nodes.input) {
            value = this.nodes.input.value;
        }

        if (!value) {
            return null;
        }

        return value;
    }

    reset() {
        this.error = "";
    }

    updateErrorText(errorText: string) {
        super.updateErrorText(errorText);
        if (errorText) {
            show(this.nodes["help-text"]);
        } else {
            this.updateInline();
        }
    }

    updateInline() {
        if (!this.isConnected || !this.target) {
            return;
        }

        const showOrHide = this.isInline ? hide : show;
        showOrHide(this.nodes["label-wrapper"]);
        showOrHide(this.nodes["help-text"]);
    }

    updateValues() {
        this.usedProps.forEach((prop) => {
            this[prop] = this.dataset[prop];
        });
    }

    async connectedCallback() {
        super.connectedCallback();
        this.updateValues();
        this.updateInline();

        this.nodes.input.addEventListener("change", this);
        this.nodes.input.addEventListener("input", this);

        await this.isInitialized;
        this.dispatchEvent(
            new CustomEvent("c-form-input-string:initialized", {
                bubbles: true,
                composed: true,
                detail: {
                    name: this.dataset.name,
                    value: this.value,
                },
            })
        );
    }

    disconnectedCallback() {
        this.nodes.input.removeEventListener("change", this);
        this.nodes.input.removeEventListener("input", this);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "c-form-input-string": FormInputString;
    }
}
